import { useMemo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Banner } from '@/components/feedback/Banner'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader, CardSubtitle, CardTitle } from '@/components/ui/Card'
import { Tag } from '@/components/ui/Tag'
import { cn } from '@/lib/cn'
import { formatDate, formatNumber } from '@/lib/format'
import { refusalMessage } from '@/lib/refusals'
import {
  rateLedger,
  trancheBilling,
  type RateLedger,
} from '@/features/cases/lib/rateLedger'
import { DocumentStatusPill } from '@/features/finance/components/DocumentActions'
import type { useRaiseInvoiceDraft } from '@/features/finance/api/useRaiseInvoiceDraft'
import { toCaseDocumentFromInvoice } from '@/types/domain'
import type { CaseDetail, InvoiceSummary, ReceivableTranche } from '@/types/domain'

/* The agreed rate and the payments it is collected in — the top of the case's
 * Invoices section.
 *
 * ── The question this answers, which nothing on the case answered before ────
 *
 * "How much of what the client agreed to pay have we actually asked them for,
 * and what is left?" The facts were all on the record and in three different
 * places: the rate on the Money section, the payment schedule beside it, and
 * the invoices in a table that never mentioned either. So the ordinary act of
 * billing the second half of a 50-50 case meant reading a figure off one tab,
 * a schedule off another and doing the subtraction by hand — which is exactly
 * the arithmetic the AD-10 guard is doing server-side and refusing writes on.
 *
 * ── The link between a tranche and its invoice is computed HERE ─────────────
 *
 * Nothing is fetched for it. `InvoiceSummary.trancheId` is served precisely so
 * this join can be made client-side, and it exists because the DISPLACEMENT is
 * otherwise invisible: issuing an invoice against a tranche removes that
 * tranche from the allocation registry, so a schedule that could not name the
 * invoice which replaced a row would show the row quietly ceasing to be owed
 * and read as data going missing.
 *
 * ── A case with no agreed rate is a STATE, not an empty table ───────────────
 *
 * Registered directly, with no quote behind it: there is no rate, no lines to
 * prefill and no ceiling. An invoice may still be raised — it simply has
 * nothing to be judged against — and the card says so in a sentence instead of
 * printing a ceiling of zero, which would report every such case as fully
 * billed. The sentence is `finance.invoiceDesk.noCeilingBody`, the same one the
 * invoice page's ceiling card says it with: one rule, one wording.
 *
 * There is deliberately no "agree a rate" button on it. The rate is Finance's
 * act on the record behind an approval (FR-36, AD-13), not a control this card
 * may offer — the money section makes the same refusal in the same words.
 */

export function CaseRateSchedule({
  record,
  invoices,
  raiseInvoice,
}: {
  record: CaseDetail
  /** This case's invoices, already read by the section for its table. Passed in
   *  rather than read again: `useCaseInvoices` is one cache entry and a second
   *  caller of it is fine, but a second SOURCE of the tranche→invoice join is
   *  how the schedule and the table start disagreeing about what closed what. */
  invoices: InvoiceSummary[]
  raiseInvoice: ReturnType<typeof useRaiseInvoiceDraft>
}) {
  const { t } = useTranslation()
  const money = record.money
  const ledger = rateLedger(money, invoices)

  /* Which invoice closed which instalment. A MAP rather than a find per row:
     several drafts may legitimately name one tranche — the partial unique index
     only forbids two ISSUED ones — and a row that showed the first match and
     hid the rest would hide the very duplicate somebody needs to resolve. */
  const byTranche = useMemo(() => {
    const index = new Map<string, InvoiceSummary[]>()
    for (const invoice of invoices) {
      if (invoice.trancheId === null) continue
      index.set(invoice.trancheId, [...(index.get(invoice.trancheId) ?? []), invoice])
    }
    return index
  }, [invoices])

  return (
    <Card>
      <CardHeader
        action={
          record.quoteNumber === undefined ? null : (
            <Tag>{t('cases.invoices.fromQuote', { quote: record.quoteNumber })}</Tag>
          )
        }
      >
        <CardTitle>{t('cases.invoices.rateTitle')}</CardTitle>
        <CardSubtitle>{t('cases.invoices.rateSubtitle')}</CardSubtitle>
      </CardHeader>

      {/* A refused raise says so where the button was pressed, and stays until
          the next press clears it. Our sentence, never the server's English
          `detail` (AD-22, AD-27). */}
      {raiseInvoice.isError ? (
        <div className="px-[18px] pt-3">
          <Banner tone="negative" title={refusalMessage(raiseInvoice.error, t)} />
        </div>
      ) : null}

      {ledger === undefined ? (
        <NoRate
          raiseInvoice={raiseInvoice}
          /* A role with no commercial access receives no money block at all
             (AD-6). That is not "no rate agreed" — it is "we may not tell you"
             — and stating the unpriced-case sentence over it would be this
             screen inventing a fact about the deal. */
          withheld={money === undefined}
        />
      ) : (
        <>
          <RateBar ledger={ledger} />
          <Schedule
            caseId={record.id}
            tranches={money?.tranches ?? []}
            byTranche={byTranche}
            raiseInvoice={raiseInvoice}
          />
        </>
      )}

      {/* The sentence that makes the fork above intelligible, and the one this
          section exists to say: a case's COSTS are its own side of the deal and
          never reach the client's invoice. They are re-billed by a debit note,
          to a named counterparty, which is a different document to a different
          party. */}
      <div className="border-line-hair text-text-muted border-t px-[18px] py-2.5 text-[12px] leading-snug font-light">
        {t('cases.invoices.expensesNote')}
      </div>
    </Card>
  )
}

/** The four figures and the bar under them. */
function RateBar({ ledger }: { ledger: RateLedger }) {
  const { t } = useTranslation()
  const overspent = Number(ledger.headroom) < 0

  return (
    <div className="flex flex-col gap-2.5 px-[18px] py-4">
      <div className="flex flex-wrap items-end gap-x-10 gap-y-4">
        <Figure label={t('cases.invoices.rate')} size="lead">
          <span>{formatNumber(ledger.approved, 2)}</span>
          <span className="text-text-muted ml-1.5 text-[12px] font-light">
            {ledger.currency}
          </span>
        </Figure>
        <Figure label={t('cases.invoices.billed')}>
          {formatNumber(ledger.billed, 2)}
        </Figure>
        {/* Positive tone on money that has ARRIVED — the one place this palette
            calls a figure good news, exactly as the invoice page uses it. */}
        <Figure label={t('cases.invoices.paid')} tone="positive">
          {formatNumber(ledger.paid, 2)}
        </Figure>
        {/* Negative headroom is STATED rather than clamped: a case already past
            its rate is exactly the case somebody needs to see, and the guard
            will refuse the next charge on it either way. */}
        <Figure
          label={t('cases.invoices.headroom')}
          className="sm:ml-auto sm:items-end"
          tone={overspent ? 'negative' : undefined}
        >
          {formatNumber(ledger.headroom, 2)}
        </Figure>
      </div>

      <div aria-hidden className="bg-neutral-100 flex h-1.5 overflow-hidden rounded-full">
        <span className="bg-positive" style={{ width: `${ledger.paidShare}%` }} />
        <span className="bg-brand" style={{ width: `${ledger.awaitingShare}%` }} />
      </div>

      {/* Colour never carries meaning alone: the bar is decoration over figures
          that are printed anyway, and the legend names each band in words. */}
      <ul className="text-text-muted flex flex-wrap gap-x-4 gap-y-1 text-[11.5px] font-light">
        <Legend className="bg-positive">{t('cases.invoices.legendPaid')}</Legend>
        <Legend className="bg-brand">{t('cases.invoices.legendAwaiting')}</Legend>
        <Legend className="bg-neutral-100">{t('cases.invoices.legendFree')}</Legend>
      </ul>

      {/* What is INSIDE "billed", said once. The server counts every invoice
          line on the case, drafts included, and refuses the next charge against
          that total — so a reader who assumed only issued paper counted would
          read the headroom as bigger than the guard will allow. */}
      <p className="text-text-muted text-[11.5px] leading-snug font-light">
        {t('cases.invoices.billedCountsDrafts')}
      </p>
    </div>
  )
}

/** The payment schedule, one row per instalment. */
function Schedule({
  caseId,
  tranches,
  byTranche,
  raiseInvoice,
}: {
  caseId: string
  tranches: ReceivableTranche[]
  byTranche: Map<string, InvoiceSummary[]>
  raiseInvoice: ReturnType<typeof useRaiseInvoiceDraft>
}) {
  const { t } = useTranslation()

  /* A priced case with no series is ordinary — the schedule is materialised at
     registration (FR-33) and a case registered before its rate was agreed has
     none. It still gets the one action, because an invoice outside the series
     is a legal document rather than a workaround. */
  if (tranches.length === 0) {
    return (
      <div className="border-line-hair flex flex-wrap items-center justify-between gap-3 border-t px-[18px] py-3.5">
        <p className="text-text-muted max-w-prose text-[12.5px] leading-snug font-light">
          {t('cases.invoices.noSchedule')}
        </p>
        <Button
          size="sm"
          disabled={raiseInvoice.isPending}
          onClick={() => raiseInvoice.raise('')}
        >
          {raiseInvoice.isPending
            ? t('cases.invoices.raising')
            : t('cases.invoices.raiseInvoice')}
        </Button>
      </div>
    )
  }

  return (
    <div className="asa-scroll min-w-0 overflow-x-auto px-[18px]">
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="border-line border-b">
            <th scope="col" className="asa-eyebrow h-9">
              {t('cases.invoices.instalment')}
            </th>
            <th scope="col" className="asa-eyebrow h-9 px-3 text-right">
              {t('common.fields.amount')}
            </th>
            <th scope="col" className="asa-eyebrow h-9 px-3">
              {t('cases.invoices.expected')}
            </th>
            <th scope="col" className="asa-eyebrow h-9 px-3">
              {t('documentKind.invoice')}
            </th>
            <th scope="col" className="asa-eyebrow h-9 text-right">
              {t('common.actions.label')}
            </th>
          </tr>
        </thead>
        <tbody>
          {tranches.map((tranche) => {
            const billing = byTranche.get(tranche.id) ?? []
            /* What was ACTUALLY billed against this instalment, and whether it
               covers it. An issued invoice removes the tranche from the
               registry whatever it is for, so a 500.00 invoice against a
               2 000.00 instalment quietly takes 1 500.00 out of what the case
               expects — see `trancheBilling`. */
            const billed = trancheBilling(tranche, billing)
            return (
              <tr key={tranche.id} className="border-line-hair h-11 border-b last:border-b-0">
                <td className="text-text-heading text-[13px]">
                  {t('cases.money.trancheN', { n: tranche.sequence })}
                </td>
                <td className="asa-num text-text-heading px-3 text-right text-[13px]">
                  <span>{formatNumber(tranche.amount.amount, 2)}</span>
                  <span className="text-text-muted ml-1 text-[11.5px] font-light">
                    {tranche.amount.currency}
                  </span>
                </td>
                <td
                  className={cn(
                    'px-3 text-[13px]',
                    /* An undated tranche is not late and must not read as a
                       gap: the second half of a 50-50 case is created undated
                       on purpose, waiting for a delivery date nobody knows yet
                       (see ReceivableTranche). */
                    tranche.expectedOn === undefined
                      ? 'text-text-faint font-light'
                      : 'text-text-body',
                  )}
                >
                  {tranche.expectedOn === undefined
                    ? t('cases.invoices.undated')
                    : formatDate(tranche.expectedOn)}
                </td>
                <td className="px-3">
                  {billing.length === 0 ? (
                    <span className="text-text-faint text-[12.5px] font-light">
                      {t('cases.invoices.notBilled')}
                    </span>
                  ) : (
                    <span className="flex flex-col gap-1">
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        {billing.map((invoice) => (
                          <InvoiceRef key={invoice.id} caseId={caseId} invoice={invoice} />
                        ))}
                      </span>
                      {/* The gap, named where it can be seen. Without this the
                          row printed the INSTALMENT's figure beside an invoice
                          for a different one, which reads as "2 000.00,
                          billed" — and the 1 500.00 nobody is going to ask for
                          is invisible on the one screen whose job is to say
                          what is still owed. */}
                      {billed?.short === true ? (
                        <span className="text-attention text-[11.5px] leading-snug">
                          {t('cases.invoices.billedShort', {
                            billed: formatNumber(billed.billed, 2),
                            expected: formatNumber(tranche.amount.amount, 2),
                            currency: tranche.amount.currency,
                          })}
                        </span>
                      ) : null}
                    </span>
                  )}
                </td>
                <td className="text-right">
                  {billing.length === 0 ? (
                    <Button
                      size="sm"
                      disabled={raiseInvoice.isPending}
                      onClick={() => raiseInvoice.raise(tranche.id)}
                    >
                      {raiseInvoice.isPending
                        ? t('cases.invoices.raising')
                        : t('cases.invoices.raiseInvoice')}
                    </Button>
                  ) : (
                    /* The row is already answered, so what is offered is the
                       way to the paper rather than a second way to raise one.
                       A link and not a button: it is a place, and the reader
                       may want it in another tab. */
                    <Link
                      to={`/cases/${caseId}/invoices/${billing[0]!.id}`}
                      className="text-text-link hover:text-text-link-hover text-[12.5px]"
                    >
                      {t('common.actions.open')}
                    </Link>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/** One invoice on a schedule row: its number, or the word that stands where a
 *  number will be, and where it stands today. */
function InvoiceRef({ caseId, invoice }: { caseId: string; invoice: InvoiceSummary }) {
  const { t } = useTranslation()
  return (
    <span className="flex items-center gap-2">
      <Link
        to={`/cases/${caseId}/invoices/${invoice.id}`}
        className="text-brand hover:text-text-link-hover font-mono text-[12.5px] font-semibold"
      >
        {/* A draft has NO number — minting belongs to the issuing transaction
            (AD-12) — so the word stands where the number will be, as a label
            and never as a value. */}
        {invoice.invoiceNumber ?? t('finance.documents.preview.draftBadge')}
      </Link>
      {/* The invoice's OWN figure, beside its number. The column to the left
          states what the instalment is worth, and the two are frequently not
          the same number — which was impossible to see while only one of them
          was on the row. */}
      <span className="asa-num text-text-body text-[12.5px]">
        {formatNumber(invoice.totalAmount, 2)}
        <span className="text-text-muted ml-1 text-[11px] font-light">
          {invoice.currency}
        </span>
      </span>
      <DocumentStatusPill record={toCaseDocumentFromInvoice(invoice)} />
    </span>
  )
}

/** The case nobody priced: what is missing, and the one thing still possible. */
function NoRate({
  raiseInvoice,
  withheld,
}: {
  raiseInvoice: ReturnType<typeof useRaiseInvoiceDraft>
  withheld: boolean
}) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-wrap items-center gap-4 px-[18px] py-4">
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span className="text-text-heading text-[14px]">
          {withheld
            ? t('cases.invoices.rateWithheldTitle')
            : t('finance.invoiceDesk.noCeilingTitle')}
        </span>
        <span className="text-text-muted max-w-prose text-[12.5px] leading-snug font-light">
          {withheld
            ? t('common.suppressed.reason')
            : t('finance.invoiceDesk.noCeilingBody')}
        </span>
      </div>
      {withheld ? null : (
        <Button
          size="sm"
          className="shrink-0"
          disabled={raiseInvoice.isPending}
          onClick={() => raiseInvoice.raise('')}
        >
          {raiseInvoice.isPending
            ? t('cases.invoices.raising')
            : t('cases.invoices.raiseInvoice')}
        </Button>
      )}
    </div>
  )
}

function Figure({
  label,
  children,
  size,
  tone,
  className,
}: {
  label: string
  children: ReactNode
  size?: 'lead'
  tone?: 'positive' | 'negative'
  className?: string
}) {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <span className="asa-eyebrow">{label}</span>
      {/* Weight 600 is allowed on a computed total, which is what all four of
          these are; nothing else on the card carries it. */}
      <span
        className={cn(
          'asa-num font-semibold',
          size === 'lead'
            ? 'text-[22px] leading-none tracking-[-0.5px]'
            : 'text-[16px] leading-none',
          tone === 'positive'
            ? 'text-positive'
            : tone === 'negative'
              ? 'text-negative'
              : 'text-text-heading',
        )}
      >
        {children}
      </span>
    </div>
  )
}

function Legend({ className, children }: { className: string; children: ReactNode }) {
  return (
    <li className="flex items-center gap-1.5">
      <span aria-hidden className={cn('size-[7px] shrink-0 rounded-full', className)} />
      {children}
    </li>
  )
}
